import type { TFunction } from '@multaqa/i18n'
import { z } from 'zod'

function primaryContactSchema(t: TFunction) {
  return z.preprocess(
    (value) => {
      if (typeof value !== 'object' || value === null) return value

      const contact = value as Record<string, unknown>
      const isBlank = (field: unknown) =>
        field === null || field === undefined || (typeof field === 'string' && field.trim() === '')

      return isBlank(contact.name) &&
        isBlank(contact.role) &&
        isBlank(contact.phone) &&
        isBlank(contact.email)
        ? undefined
        : value
    },
    z
      .object({
        name: z.string().min(1, t('customer.validation.contactNameRequired')),
        role: z.string().optional().nullable(),
        phone: z.string().optional().nullable(),
        email: z
          .union([z.string().email(t('customer.validation.emailInvalid')), z.string().length(0)])
          .optional()
          .nullable(),
        is_primary: z.boolean(),
      })
      .optional(),
  )
}

function customerFields(t: TFunction) {
  return z
    .object({
      name: z.object({
        en: z.string().optional().nullable(),
        ar: z.string().optional().nullable(),
      }),
      type: z.enum(['company', 'individual']),
      address: z.object({
        en: z.string().optional().nullable(),
        ar: z.string().optional().nullable(),
      }),
      receiver_type: z.enum(['B', 'P', 'F']).optional().nullable(),
      governorate: z.string().optional().nullable(),
      city: z.string().optional().nullable(),
      street: z.string().optional().nullable(),
      building_number: z.string().optional().nullable(),
      country: z.string().min(1, t('customer.validation.countryRequired')),
      tax_identifier: z.string().optional().nullable(),
      payment_terms_id: z.string().optional().nullable(),
      payment_terms: z.string().optional().nullable(),
      default_currency: z.string().min(3).max(3),
      tax_treatment: z.string().optional().nullable(),
      salesperson_user_id: z.string().optional().nullable(),
      department_id: z.string().optional().nullable(),
      is_intercompany: z.boolean(),
      target_company_id: z.string().optional().nullable(),
      is_active: z.boolean(),
      is_walk_in: z.boolean(),
      mobile: z.string().optional().nullable(),
      till_note: z.string().optional().nullable(),
      primary_contact: primaryContactSchema(t),
    })
    .refine((data) => data.name.en?.trim() !== '' || data.name.ar?.trim() !== '', {
      message: t('customer.validation.nameRequired'),
      path: ['name', 'en'],
    })
}

export function customerFormSchemaForLocale(t: TFunction, locale: 'en' | 'ar') {
  return customerFields(t)
    .refine((data) => Boolean(data.name[locale]?.trim()), {
      message: t('customer.validation.nameRequired'),
      path: ['name', locale],
    })
    .refine((data) => Boolean(data.address[locale]?.trim()), {
      message: t('customer.validation.addressRequired'),
      path: ['address', locale],
    })
}

export function customerFormSchema(t: TFunction) {
  return customerFormSchemaForLocale(t, 'en')
}

export type CustomerFormValues = z.infer<ReturnType<typeof customerFormSchema>>

export function contactFormSchema(t: TFunction) {
  return z.object({
    name: z.string().min(1, t('customer.validation.contactNameRequired')),
    role: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    email: z
      .union([z.string().email(t('customer.validation.emailInvalid')), z.string().length(0)])
      .optional()
      .nullable(),
    is_primary: z.boolean(),
  })
}

export type ContactFormValues = z.infer<ReturnType<typeof contactFormSchema>>
